import { observer } from 'mobx-react-lite';

import IMessageLogo from '@/assets/images/channels/imessage.png';
import WhatsAppLogo from '@/assets/images/channels/whatsapp.png';
import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ChannelPersonaEditor } from '@/features/msg-channels/common/channel-persona-editor';
import {
  useChannelConnectionQuery,
  useDisconnectChannelMutation,
} from '@/features/msg-channels/common/msg-channels.query';
import {
  MESSAGING_CHANNEL_LABELS,
  type MessagingChannelType,
} from '@/features/msg-channels/common/msg-channels.types';
import { IMessageChannelConnectModal } from '@/features/msg-channels/imessage/IMessageChannelConnectModal';
import { WhatsAppChannelConnectModal } from '@/features/msg-channels/whatsapp/WhatsAppChannelConnectModal';
import { userStore } from '@/stores/user.store';

import type { ConfigurationContext } from './configuration.types';

const CHANNEL_ICON_SRC: Record<MessagingChannelType, string> = {
  imessage: IMessageLogo,
  whatsapp_business: WhatsAppLogo,
};

const CHANNEL_CONNECT_MODAL = {
  imessage: IMessageChannelConnectModal,
  whatsapp_business: WhatsAppChannelConnectModal,
} satisfies Record<MessagingChannelType, unknown>;

function ChannelConnectionSettings({
  channel,
}: {
  channel: MessagingChannelType;
}) {
  const label = MESSAGING_CHANNEL_LABELS[channel];
  const {
    data: connection,
    isLoading,
    isLoadingError,
    isRefetchError,
    isFetching,
    refetch,
  } = useChannelConnectionQuery(channel);
  const disconnectMutation = useDisconnectChannelMutation(channel);

  const identifier = connection?.identifier;
  const isChecking = isLoading || (isLoadingError && isFetching);

  const handleDisconnect = async () => {
    const confirmed = await ModalsManager.showResolvable(ConfirmDialog, {
      title: `Disconnect ${label}?`,
      description: `Littlebird will stop responding to messages on ${label}. You can reconnect anytime.`,
      confirmText: 'Disconnect',
    });
    if (!confirmed) return;
    await disconnectMutation.mutateAsync();
  };

  return (
    <YStack className="gap-3">
      <h2 className="text-sm font-medium text-foreground">{label}</h2>
      <XStack className="items-center justify-between gap-6 rounded-lg border border-border bg-surface px-4 py-3.5 dark:border-border-strong">
        <XStack className="min-w-0 items-center gap-3.5">
          <img
            src={CHANNEL_ICON_SRC[channel]}
            alt=""
            className="block size-8 shrink-0 rounded-[0.45rem] object-cover"
          />
          <YStack className="min-w-0 gap-1">
            <p className="truncate text-sm text-foreground">
              {identifier ??
                (isLoadingError
                  ? "Couldn't check connection status"
                  : 'Not connected')}
            </p>
            {isRefetchError && (
              <p className="truncate text-xs text-danger-11">
                Couldn't refresh connection status
              </p>
            )}
          </YStack>
        </XStack>
        <XStack className="shrink-0 gap-2">
          <Button
            variant={identifier ? 'outline' : 'default'}
            size="sm"
            disabled={isChecking}
            onClick={
              isLoadingError
                ? () => void refetch()
                : () =>
                    ModalsManager.show(CHANNEL_CONNECT_MODAL[channel], {
                      currentIdentifier: identifier ?? null,
                    })
            }
          >
            {isChecking
              ? 'Checking...'
              : isLoadingError
                ? 'Retry'
                : identifier
                  ? `Manage ${label}`
                  : `Connect ${label}`}
          </Button>
          {identifier && (
            <Button
              variant="outline"
              size="sm"
              disabled={disconnectMutation.isPending}
              onClick={() => void handleDisconnect()}
            >
              Disconnect
            </Button>
          )}
        </XStack>
      </XStack>
    </YStack>
  );
}

function channelOf(context: ConfigurationContext): MessagingChannelType {
  if (context.localSource?.kind !== 'channel') {
    throw new Error(
      `ChannelConfiguration requires a channel source, got "${context.localSource?.kind}"`
    );
  }
  return context.localSource.channel;
}

export const ChannelConfiguration = observer(function ChannelConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const channel = channelOf(context);

  return (
    <YStack className="gap-6 p-6">
      <ChannelConnectionSettings channel={channel} />
      {userStore.isInternalUser && (
        <ChannelPersonaEditor description="These persona settings apply to all messaging channels." />
      )}
    </YStack>
  );
});
